import type { HouseholdEvent, NoticeReason, Urgency } from "@tutor/protocol";
import { useEffect, useRef } from "react";
import { z } from "zod";

// The household live channel (apps/api/src/household-live.ts): a WebSocket carrying the
// parent's cookie, subprotocol `tutor.household.v1`. The last sequence number stays in
// memory and every reconnect asks for the gap with `?since=<seq>`.

export const HOUSEHOLD_PROTOCOL = "tutor.household.v1";

const reason = z.enum([
  "safetyLow",
  "safetyHigh",
  "frustration",
  "plateau",
  "dailyCap",
]) satisfies z.ZodType<NoticeReason>;
const urgency = z.enum(["none", "digest", "now"]) satisfies z.ZodType<Urgency>;

const event = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("parent.alert"),
    alertId: z.string(),
    profileId: z.string(),
    urgency,
    reason,
    skillId: z.string().nullable(),
    pauseSession: z.boolean(),
  }),
  z.object({ type: z.literal("goals.updated"), profileId: z.string(), version: z.number() }),
  z.object({ type: z.literal("consent.updated"), profileId: z.string() }),
]) satisfies z.ZodType<HouseholdEvent>;

const eventFrame = z.looseObject({ type: z.literal("event"), seq: z.number(), event });

export function householdLiveUrl(since: number): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/v1/households/live?since=${String(since)}`;
}

/** Opens a socket; injectable for tests. */
export type SocketFactory = (url: string, protocol: string) => WebSocket;

const defaultFactory: SocketFactory = (url, protocol) => new WebSocket(url, protocol);

/** Reconnect delay after `failures` consecutive failures: 1 s doubling to 30 s, with jitter. */
export function backoffMs(failures: number): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.min(failures, 5));
  return base / 2 + Math.random() * (base / 2);
}

/**
 * Keeps the household channel open while `enabled`, calling `onEvent` once per sequenced
 * event (duplicates after a replay are dropped).
 */
export function useHouseholdLive(
  enabled: boolean,
  onEvent: (event: HouseholdEvent) => void,
  createSocket: SocketFactory = defaultFactory,
): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const lastSeq = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      try {
        socket = createSocket(householdLiveUrl(lastSeq.current), HOUSEHOLD_PROTOCOL);
      } catch {
        retry();
        return;
      }
      socket.onopen = () => {
        failures = 0;
      };
      socket.onmessage = (message: MessageEvent) => {
        if (typeof message.data !== "string") return;
        let json: unknown;
        try {
          json = JSON.parse(message.data);
        } catch {
          return;
        }
        const frame = eventFrame.safeParse(json);
        if (!frame.success || frame.data.seq <= lastSeq.current) return;
        lastSeq.current = frame.data.seq;
        handler.current(frame.data.event);
      };
      socket.onclose = () => {
        socket = null;
        retry();
      };
    };

    const retry = () => {
      if (stopped) return;
      timer = setTimeout(connect, backoffMs(failures));
      failures += 1;
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      if (socket !== null) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [enabled, createSocket]);
}
